'use client';

import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import { AhafoodLogo } from '@ahamove/icons/order';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Facebook, Loader2, X } from 'lucide-react';
import Image from 'next/image';

import { CustomOTPForm } from '../login-otp/custom-otp-form';
import { PhoneLoginForm } from '../login-otp/phone-login-form';
import { AuthSuccess } from '../login-otp/success';
import { useLoginDialog } from './use-login-dialog';
import { useIsMobile } from '@/lib/is-mobile';

interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: string;
}

const BackgroundDecorations = ({ isMobile }: { isMobile: boolean }) => (
  <>
    <div
      className={cn(
        'absolute bg-primary-20 rounded-full',
        isMobile
          ? 'top-8 -left-8 size-32 opacity-30'
          : 'top-14 -left-16 size-56 opacity-40'
      )}
    />
  </>
);

const RobotImage = ({ isMobile }: { isMobile: boolean }) => (
  <div
    className={cn(
      'relative flex justify-end',
      isMobile ? 'pt-4 pr-4 pb-2' : 'items-center justify-center min-h-[12vh]'
    )}
  >
    <div
      className={cn(
        'relative',
        isMobile ? 'size-24' : 'size-52 -top-0.5 -right-40 z-50'
      )}
    >
      <Image
        src={`${assetPrefix}/static/images/robot-like.png`}
        alt="Chat bot assistant"
        fill
        className={cn(
          'object-contain',
          isMobile ? 'rotate-[-15deg]' : 'rotate-[-20deg]'
        )}
        priority
      />
    </div>
  </div>
);

const FoodEmojis = ({ isMobile }: { isMobile: boolean }) => {
  const FOOD_EMOJIS = ['🍕', '🍔', '🥗', '🍜', '🧋'];
  // Include the full bg- classes so Tailwind can pick them up
  const EMOJI_COLORS = [
    'bg-red-20',
    'bg-yellow-20',
    'bg-green-20',
    'bg-blue-20',
    'bg-secondary-20',
  ];

  return (
    <div
      className={cn(
        'flex justify-between opacity-40 w-full',
        isMobile ? 'pt-2' : 'pt-4'
      )}
    >
      {FOOD_EMOJIS.map((emoji, index) => (
        <Badge
          key={emoji}
          className={cn(
            EMOJI_COLORS[index],
            'rounded-full flex items-center justify-center transition-transform hover:scale-110 duration-200',
            isMobile ? 'size-10' : 'size-12'
          )}
        >
          {emoji}
        </Badge>
      ))}
    </div>
  );
};

const LoginHeader = ({ isMobile }: { isMobile: boolean }) => (
  <>
    <div className="flex justify-center mb-4">
      <AhafoodLogo
        className={cn('mx-auto', isMobile ? 'w-1/2 h-12' : 'w-2/3 h-16')}
      />
    </div>
    <div className="text-center space-y-3">
      <h2
        className={cn(
          'font-bold text-secondary-60',
          isMobile ? 'text-xl' : 'text-2xl'
        )}
      >
        Đăng nhập ngay
      </h2>
      <p
        className={cn(
          'text-neutral-60 leading-relaxed text-xs',
          isMobile ? 'px-2' : ''
        )}
      >
        Chỉ sau 1 thao tác, bạn có thể đặt đơn nhanh chóng, được AI gợi ý ngay
        món theo sở thích và ưu đãi chỉ dành riêng cho bạn.
      </p>
    </div>
  </>
);

const LoginActions = ({
  isMobile,
  onFacebookLogin,
  onClose,
  isFBLoading,
  isRequestingOTP,
}: {
  isMobile: boolean;
  onFacebookLogin: () => void;
  onClose: () => void;
  isFBLoading: boolean;
  isRequestingOTP: boolean;
}) => (
  <div className={cn(isMobile ? 'space-y-3' : 'space-y-4')}>
    <Button
      onClick={onFacebookLogin}
      disabled={isFBLoading}
      className={cn(
        'w-full bg-blue-60 hover:bg-blue-70 text-white font-semibold rounded-xl',
        'flex items-center justify-center gap-3 shadow-lg transition-all duration-200',
        isMobile ? 'h-11 text-sm' : 'h-12 text-base'
      )}
    >
      {isFBLoading ? (
        <>
          <Loader2 className="size-5 animate-spin" />
          <span>Đang đăng nhập...</span>
        </>
      ) : (
        <>
          <Facebook width={20} height={20} />
          <span>Tiếp tục với Facebook</span>
        </>
      )}
    </Button>

    <Button
      variant="ghost"
      onClick={onClose}
      disabled={isFBLoading || isRequestingOTP}
      className={cn(
        'w-full text-neutral-50 hover:text-neutral-70 underline transition-colors duration-200',
        isMobile ? 'h-9 text-sm' : 'h-10'
      )}
    >
      Để sau, tiếp tục xem món
    </Button>
  </div>
);

const LoginContent = ({
  isMobile,
  onDialogClose,
}: {
  isMobile: boolean;
  onDialogClose?: () => void;
}) => {
  const {
    currentStep,
    phoneNumber,
    phoneError,
    isRequestingOTP,
    isFBLoading,
    handlePhoneSuccess,
    handleOTPSuccess,
    handleBackToPhone,
    handleFacebookLogin,
    handleClose,
    handleSuccess,
    setPhoneError,
  } = useLoginDialog();

  const renderStepContent = () => {
    switch (currentStep) {
      case 'phone':
        return (
          <div className={cn('space-y-6', isMobile && 'space-y-4')}>
            <LoginHeader isMobile={isMobile} />

            <PhoneLoginForm
              onSuccess={handlePhoneSuccess}
              isLoading={isRequestingOTP}
              error={phoneError}
              onClearError={() => setPhoneError('')}
            />

            <LoginActions
              isMobile={isMobile}
              onFacebookLogin={handleFacebookLogin}
              onClose={() => {
                handleClose();
                onDialogClose?.();
              }}
              isFBLoading={isFBLoading}
              isRequestingOTP={isRequestingOTP}
            />

            <FoodEmojis isMobile={isMobile} />
          </div>
        );

      case 'otp':
        return (
          <CustomOTPForm
            phoneNumber={phoneNumber}
            onSuccess={handleOTPSuccess}
            onBack={handleBackToPhone}
          />
        );

      case 'success':
        return (
          <AuthSuccess
            phoneNumber={phoneNumber}
            onContinue={() => {
              onDialogClose?.();
              handleSuccess();
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col relative overflow-x-hidden',
        isMobile ? 'h-full min-h-0 flex-1' : 'min-h-full flex-1'
      )}
    >
      <BackgroundDecorations isMobile={isMobile} />

      {!isMobile && (
        <div className="flex-1 flex items-center justify-center relative min-h-[12vh]">
          <RobotImage isMobile={false} />
        </div>
      )}

      <div
        className={cn(
          'bg-white shadow-2xl w-full relative z-30',
          isMobile
            ? 'rounded-t-2xl p-4 flex-1 min-h-0'
            : 'rounded-t-3xl p-6 min-h-[50vh] flex items-end'
        )}
      >
        {isMobile && <RobotImage isMobile={true} />}

        <div className="w-full">{renderStepContent()}</div>
      </div>
    </div>
  );
};

export function LoginDialog({
  open,
  onOpenChange,
  location,
}: LoginDialogProps) {
  const isMobile = useIsMobile();

  const commonProps = {
    open,
    onOpenChange,
    onPointerDownOutside: (e: Event) => e.preventDefault(),
  };

  if (isMobile) {
    return (
      <Sheet {...commonProps}>
        <SheetContent
          side="bottom"
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="p-0 bg-gradient-to-br from-primary-40 via-primary-30 to-yellow-20 border-none overflow-hidden max-h-[95vh] h-auto rounded-t-xl"
        >
          <SheetTitle className="hidden">Đăng nhập</SheetTitle>
          <div className="overflow-y-auto relative">
            <X
              className=" absolute top-4 right-4 cursor-pointer size-5 text-neutral-50 z-50 bg-neutral-10 rounded-full"
              onClick={() => onOpenChange(false)}
            />
            <LoginContent
              isMobile={true}
              onDialogClose={() => onOpenChange(false)}
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog {...commonProps}>
      <DialogContent className="border-none sm:max-w-md p-0 gap-0 overflow-hidden h-[700px] bg-gradient-to-br from-primary-40 via-primary-30 to-yellow-20">
        <DialogTitle className="hidden">Đăng nhập</DialogTitle>
        <LoginContent
          isMobile={false}
          onDialogClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
